const PUBLIC_SEO = require('./publicSeo.json');
const ABOUT_NOESA = require('./aboutNoesa.json');
const PUBLIC_NEWS = require('./publicNews');
const { getPublications, getPublicationsUi } = require('./publicPublications');
const PUBLIC_IDENTITY = require('./publicIdentity.json');
const { getMobileContent } = require('./mobileSite');
const { LANDING_BASE_PATHS, LANDING_KEYS, getLandingContent } = require('./seoLandings');

const ORIGIN = 'https://noesa.ai';
const LANGUAGES = Object.freeze(['ru', 'en']);
const IN_LANGUAGE = Object.freeze({ ru: 'ru-RU', en: 'en-US' });
const OG_LOCALE = Object.freeze({ ru: 'ru_RU', en: 'en_US' });

const CODE_RELEASE = Object.freeze({
  version: '0.2.28',
  downloadUrl: 'https://api.noesa.ai/code/desktop/preview/releases/noesa-code-0.2.28-setup.exe',
});

const PLAN_PRICES = Object.freeze({
  free: Object.freeze({ month: 0, half: 0, year: 0 }),
  plus: Object.freeze({ month: 449, half: 2245, year: 4490 }),
  pro: Object.freeze({ month: 899, half: 4495, year: 8990 }),
  max: Object.freeze({ month: 6990, half: 34950, year: 69900 }),
});

const CORE_BASE_PATHS = Object.freeze({
  welcome: '/',
  aiPlanner: '/ai-planner',
  pricing: '/pricing',
  code: '/code',
  about: '/about',
  mobile: '/mobile',
  ...LANDING_BASE_PATHS,
});
const CORE_KEYS = Object.freeze(Object.keys(CORE_BASE_PATHS));
const NAVIGATION_KEYS = Object.freeze(['welcome', 'code', 'mobile', 'about', 'pricing']);
const NAVIGATION_LABELS = Object.freeze({
  ru: Object.freeze(['Noesa AI', 'Noesa Code', 'Мобильное приложение', 'Кто мы', 'Тарифы']),
  en: Object.freeze(['Noesa AI', 'Noesa Code', 'Mobile app', 'Who we are', 'Pricing']),
});
const NEWS_INDEX_KEY = 'news';
const NEWS_KEY_PREFIX = 'news:';

const ORGANIZATION_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;
const NOESA_APP_ID = `${ORIGIN}/#noesa-app`;
const CODE_APP_ID = `${ORIGIN}/code#software`;

function normalizeLanguage(language) {
  return String(language || '').toLowerCase().startsWith('en') ? 'en' : 'ru';
}

function splitPathSuffix(input) {
  const value = String(input || '/');
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  const queryAt = value.indexOf('?');
  const hashAt = value.indexOf('#');
  const positions = [queryAt, hashAt].filter((position) => position >= 0);
  const suffixAt = positions.length ? Math.min(...positions) : value.length;
  return { pathname: value.slice(0, suffixAt) || '/', suffix: value.slice(suffixAt) };
}

function normalizePathname(input) {
  const split = splitPathSuffix(input);
  if (!split) return null;
  let pathname = split.pathname;
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '');
  return pathname || '/';
}

function routeKeyFromBasePath(basePath) {
  const coreEntry = Object.entries(CORE_BASE_PATHS).find(([, path]) => path === basePath);
  if (coreEntry) return coreEntry[0];
  if (basePath === '/news') return NEWS_INDEX_KEY;
  const articleMatch = /^\/news\/([^/]+)$/.exec(basePath);
  if (articleMatch && PUBLIC_NEWS.NEWS_SLUGS.includes(articleMatch[1])) {
    return `${NEWS_KEY_PREFIX}${articleMatch[1]}`;
  }
  return null;
}

function basePathForKey(key) {
  if (CORE_BASE_PATHS[key]) return CORE_BASE_PATHS[key];
  if (key === NEWS_INDEX_KEY) return '/news';
  if (String(key || '').startsWith(NEWS_KEY_PREFIX)) {
    const slug = String(key).slice(NEWS_KEY_PREFIX.length);
    if (PUBLIC_NEWS.NEWS_SLUGS.includes(slug)) return `/news/${slug}`;
  }
  return null;
}

function localizedPath(basePath, language) {
  const normalizedLanguage = normalizeLanguage(language);
  if (normalizedLanguage === 'ru') return basePath;
  return basePath === '/' ? '/en' : `/en${basePath}`;
}

function resolvePublicRoute(input) {
  const pathname = normalizePathname(input);
  if (!pathname) return null;
  const isEnglish = pathname === '/en' || pathname.startsWith('/en/');
  const language = isEnglish ? 'en' : 'ru';
  const basePath = isEnglish ? (pathname.slice(3) || '/') : pathname;
  const key = routeKeyFromBasePath(basePath);
  if (!key) return null;
  return { key, language, path: localizedPath(basePath, language) };
}

function publicPath(input, language = 'ru') {
  const split = splitPathSuffix(input);
  if (!split) return null;
  const route = resolvePublicRoute(split.pathname);
  if (!route) return null;
  const basePath = basePathForKey(route.key);
  return `${localizedPath(basePath, language)}${split.suffix}`;
}

// A calendar and planner describe the same feature. Keep one indexable page,
// while preserving attribution and in-page anchors for old/ad hoc links.
function getPublicRedirect(input) {
  const split = splitPathSuffix(input);
  if (!split) return null;
  const pathname = normalizePathname(split.pathname);
  if (pathname === '/ai-calendar') return `/ai-planner${split.suffix}`;
  if (pathname === '/en/ai-calendar') return `/en/ai-planner${split.suffix}`;
  return null;
}

// Shared by the interactive navigation and pre-rendered HTML. Features such as
// the AI planner keep their own URLs without becoming a separate product here.
function getPublicNavigation(language = 'ru') {
  const locale = normalizeLanguage(language);
  return NAVIGATION_KEYS.map((key, index) => ({
    key,
    path: localizedPath(CORE_BASE_PATHS[key], locale),
    label: NAVIGATION_LABELS[locale][index],
  }));
}

function absoluteUrl(path) {
  return new URL(path || '/', ORIGIN).toString();
}

function getHreflangLinks(pageOrKey) {
  const key = typeof pageOrKey === 'string' ? pageOrKey : pageOrKey?.key;
  const basePath = basePathForKey(key);
  if (!basePath) return [];
  const ru = absoluteUrl(localizedPath(basePath, 'ru'));
  const en = absoluteUrl(localizedPath(basePath, 'en'));
  return [
    { hreflang: 'ru', href: ru },
    { hreflang: 'en', href: en },
    { hreflang: 'x-default', href: ru },
  ];
}

function formatRubles(value, language) {
  const amount = Number(value || 0).toLocaleString(language === 'en' ? 'en-US' : 'ru-RU');
  return language === 'en' ? `${amount} RUB/month` : `${amount} ₽/мес`;
}

function decoratePage(key, language, source) {
  const normalizedLanguage = normalizeLanguage(language);
  const basePath = basePathForKey(key);
  if (!basePath || !source) return null;
  const path = localizedPath(basePath, normalizedLanguage);
  const canonical = absoluteUrl(path);
  const hreflang = Object.fromEntries(getHreflangLinks(key).map((entry) => [entry.hreflang, entry.href]));
  const page = {
    ...source,
    key,
    language: normalizedLanguage,
    inLanguage: IN_LANGUAGE[normalizedLanguage],
    ogLocale: OG_LOCALE[normalizedLanguage],
    basePath,
    path,
    canonical,
    hreflang,
    ogImage: source.ogImage || '/icon-512.png',
    ogImageUrl: absoluteUrl(source.ogImage || '/icon-512.png'),
  };

  if (key === 'pricing') {
    page.plans = (source.plans || []).map((plan) => ({
      ...plan,
      prices: PLAN_PRICES[plan.key],
    }));
    page.sections = page.plans.map((plan) => [
      `${plan.name} — ${formatRubles(plan.prices?.month, normalizedLanguage)}`,
      plan.description,
    ]);
  }

  if (key === 'code') {
    page.release = CODE_RELEASE;
    page.cta = normalizedLanguage === 'en'
      ? ['Download Noesa Code for Windows', CODE_RELEASE.downloadUrl]
      : ['Скачать Noesa Code для Windows', CODE_RELEASE.downloadUrl];
  }

  return page;
}

function getCorePage(key, language) {
  const normalizedLanguage = normalizeLanguage(language);
  if (LANDING_KEYS.includes(key)) {
    const source = getLandingContent(key, normalizedLanguage);
    if (!source) throw new Error(`Missing landing translation: ${key}/${normalizedLanguage}`);
    return decoratePage(key, normalizedLanguage, {
      ...source,
      application: 'noesa',
      schemaType: 'WebPage',
      ogType: 'website',
    });
  }
  if (key === 'mobile') {
    return decoratePage(key, normalizedLanguage, {
      ...getMobileContent(normalizedLanguage),
      // This is a release information page, not a claim of an available app.
      schemaType: 'WebPage',
      ogType: 'website',
    });
  }
  if (key === 'about') {
    const source = ABOUT_NOESA.locales?.[normalizedLanguage];
    const publications = getPublications(normalizedLanguage);
    return decoratePage(key, normalizedLanguage, {
      ...source,
      milestones: source.milestones.map((milestone) => {
        const publication = publications.find((item) => item.id === milestone.publicationId);
        if (!publication) throw new Error(`Unknown About publication: ${milestone.publicationId}`);
        return { ...milestone, href: publication.href, action: publication.action, references: publication.references };
      }),
      navLabel: normalizedLanguage === 'en' ? 'Who we are' : 'Кто мы',
      sources: source.sources,
      schemaType: 'AboutPage',
      ogType: 'website',
      ogImage: '/icon-512.png',
    });
  }
  const ru = PUBLIC_SEO[key];
  if (!ru) return null;
  const translated = normalizedLanguage === 'en' ? PUBLIC_SEO.locales?.en?.[key] : null;
  if (normalizedLanguage === 'en' && !translated) {
    throw new Error(`Missing public page translation: ${key}/en`);
  }
  return decoratePage(key, normalizedLanguage, { ...ru, ...(translated || {}) });
}

function getNewsPage(key, language) {
  const normalizedLanguage = normalizeLanguage(language);
  if (key === NEWS_INDEX_KEY) {
    const publications = getPublications(normalizedLanguage);
    return decoratePage(key, normalizedLanguage, {
      ...getPublicationsUi(normalizedLanguage),
      sections: publications.map((publication) => [publication.title, publication.description]),
      publications,
      schemaType: 'CollectionPage',
      ogType: 'website',
      ogImage: '/icon-512.png',
    });
  }

  if (!String(key || '').startsWith(NEWS_KEY_PREFIX)) return null;
  const slug = String(key).slice(NEWS_KEY_PREFIX.length);
  const article = PUBLIC_NEWS.getNewsArticle(slug, normalizedLanguage);
  if (!article) return null;
  return decoratePage(key, normalizedLanguage, {
    ...article,
    title: `${article.title} — noesa`,
    h1: article.title,
    lead: article.description,
    navLabel: article.title,
    article,
    schemaType: 'BlogPosting',
    ogType: 'article',
    ogImage: article.heroImage || '/icon-512.png',
  });
}

function getPublicPage(key, language = 'ru') {
  if (CORE_KEYS.includes(key)) return getCorePage(key, language);
  return getNewsPage(key, language);
}

function getPublicPageFromPath(pathname) {
  const route = resolvePublicRoute(pathname);
  return route ? getPublicPage(route.key, route.language) : null;
}

function getNoesaOffers(language, pricingPage) {
  const page = pricingPage || getPublicPage('pricing', language);
  return (page?.plans || []).map((plan) => ({
    '@type': 'Offer',
    '@id': `${ORIGIN}/#offer-${plan.key}`,
    name: language === 'en' ? `${plan.name} monthly plan` : `Тариф ${plan.name} на месяц`,
    price: String(plan.prices?.month ?? 0),
    priceCurrency: 'RUB',
    url: page.canonical,
    description: plan.description,
    itemOffered: { '@id': NOESA_APP_ID },
  }));
}

function getStructuredData(page) {
  if (!page?.key || !page.canonical || !LANGUAGES.includes(page.language)) return null;
  const websiteUrl = `${ORIGIN}/`;
  const home = getPublicPage('welcome', page.language);
  const pricing = getPublicPage('pricing', page.language);
  const codePage = getPublicPage('code', page.language);
  const noesaOffers = page.key === 'pricing' ? getNoesaOffers(page.language, pricing) : [];
  const pageId = `${page.canonical}#webpage`;
  const graph = [
    {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: PUBLIC_IDENTITY.name,
      alternateName: PUBLIC_IDENTITY.alternateName,
      url: PUBLIC_IDENTITY.url,
      email: PUBLIC_IDENTITY.email,
      sameAs: PUBLIC_IDENTITY.sameAs,
      logo: { '@type': 'ImageObject', '@id': `${ORIGIN}/#logo`, url: PUBLIC_IDENTITY.logoUrl },
      ...(noesaOffers.length
        ? { makesOffer: noesaOffers.map((offer) => ({ '@id': offer['@id'] })) }
        : {}),
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      name: 'Noesa.ai',
      url: websiteUrl,
      inLanguage: LANGUAGES.map((language) => IN_LANGUAGE[language]),
      publisher: { '@id': ORGANIZATION_ID },
    },
    ...noesaOffers,
  ];

  const pageNode = {
    '@type': page.schemaType === 'AboutPage' || page.schemaType === 'CollectionPage'
      ? page.schemaType
      : 'WebPage',
    '@id': pageId,
    url: page.canonical,
    name: page.title,
    description: page.description,
    inLanguage: page.inLanguage,
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    primaryImageOfPage: { '@type': 'ImageObject', url: page.ogImageUrl },
  };

  if (page.key === 'about') {
    pageNode.about = { '@id': ORGANIZATION_ID };
    pageNode.mainEntity = { '@id': ORGANIZATION_ID };
  }
  graph.push(pageNode);

  if (page.application === 'noesa') {
    const noesaApp = {
      '@type': 'SoftwareApplication',
      '@id': NOESA_APP_ID,
      name: 'Noesa AI',
      url: home.canonical,
      description: home.description,
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web',
      inLanguage: LANGUAGES.map((language) => IN_LANGUAGE[language]),
      featureList: home.features,
      publisher: { '@id': ORGANIZATION_ID },
      ...(noesaOffers.length
        ? { offers: noesaOffers.map((offer) => ({ '@id': offer['@id'] })) }
        : {}),
    };
    graph.push(noesaApp);
    pageNode.mainEntity = { '@id': NOESA_APP_ID };
  }

  if (page.application === 'code') {
    graph.push({
      '@type': 'SoftwareApplication',
      '@id': CODE_APP_ID,
      name: 'Noesa Code',
      url: page.canonical,
      description: page.description,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: ['Windows 10', 'Windows 11'],
      softwareVersion: CODE_RELEASE.version,
      downloadUrl: CODE_RELEASE.downloadUrl,
      featureList: page.features,
      publisher: { '@id': ORGANIZATION_ID },
    });
    pageNode.mainEntity = { '@id': CODE_APP_ID };
  }

  if (page.key === 'about') {
    const noesaApp = {
      '@type': 'SoftwareApplication',
      '@id': NOESA_APP_ID,
      name: 'Noesa AI',
      url: home.canonical,
      description: home.description,
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Web',
      inLanguage: LANGUAGES.map((language) => IN_LANGUAGE[language]),
      featureList: home.features,
      publisher: { '@id': ORGANIZATION_ID },
    };
    const codeApp = {
      '@type': 'SoftwareApplication',
      '@id': CODE_APP_ID,
      name: 'Noesa Code',
      url: codePage.canonical,
      description: codePage.description,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: ['Windows 10', 'Windows 11'],
      softwareVersion: CODE_RELEASE.version,
      downloadUrl: CODE_RELEASE.downloadUrl,
      featureList: codePage.features,
      publisher: { '@id': ORGANIZATION_ID },
    };
    graph.push(noesaApp, codeApp);
  }

  if (page.key === NEWS_INDEX_KEY) {
    pageNode.citation = (page.publications || []).map((publication) => ({
      '@type': 'Article',
      headline: publication.sourceTitle,
      url: publication.href,
      datePublished: publication.datePublished,
      inLanguage: 'ru-RU',
      publisher: { '@type': 'Organization', name: publication.sourcePublisher },
    }));
  }

  if (String(page.key).startsWith(NEWS_KEY_PREFIX)) {
    const article = page.article;
    const articleId = `${page.canonical}#article`;
    graph.push({
      '@type': 'BlogPosting',
      '@id': articleId,
      headline: article.title,
      description: article.description,
      image: page.ogImageUrl,
      url: page.canonical,
      mainEntityOfPage: { '@id': pageId },
      isPartOf: { '@id': WEBSITE_ID },
      inLanguage: page.inLanguage,
      datePublished: article.datePublished,
      author: { '@id': ORGANIZATION_ID },
      publisher: { '@id': ORGANIZATION_ID },
    });
    pageNode.mainEntity = { '@id': articleId };
  }

  if (page.basePath !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${page.canonical}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Noesa AI',
          item: home.canonical,
        },
        { '@type': 'ListItem', position: 2, name: page.h1, item: page.canonical },
      ],
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

function listPublicPages(languages = LANGUAGES) {
  const normalizedLanguages = Array.from(new Set((languages || LANGUAGES).map(normalizeLanguage)));
  const keys = [
    ...CORE_KEYS,
    NEWS_INDEX_KEY,
  ];
  return normalizedLanguages.flatMap((language) => keys.map((key) => getPublicPage(key, language)).filter(Boolean));
}

function serializeStructuredData(value) {
  return JSON.stringify(value)
    .replace(/&/g, '\\u0026')
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

module.exports = {
  ORIGIN,
  LANGUAGES,
  PLAN_PRICES,
  CODE_RELEASE,
  ORGANIZATION_ID,
  WEBSITE_ID,
  NOESA_APP_ID,
  CODE_APP_ID,
  PUBLIC_IDENTITY,
  normalizeLanguage,
  publicPath,
  getPublicRedirect,
  getPublicNavigation,
  resolvePublicRoute,
  getPublicPage,
  getPublicPageFromPath,
  getHreflangLinks,
  getStructuredData,
  listPublicPages,
  serializeStructuredData,
};
